import { bindTooltip, createHoverLayer, IHoverBinding, TOOLTIP_SHOW_DELAY_MS } from '@/pure/tooltip';
import placeFloating from '@/util/position';
import { DirectiveOptions } from 'vue';

let label: HTMLElement | null = null;
let stopFollowing = (): void => undefined;

const labelElement = () => {
  if (label === null) {
    label = document.createElement('div');
    label.className = 'igs-tooltip';
    label.setAttribute('role', 'tooltip');
    label.hidden = true;
    document.body.appendChild(label);
  }
  return label;
};

const layer = createHoverLayer<string>({
  delayMs: TOOLTIP_SHOW_DELAY_MS,
  isFocusShown: true,
  timers: { clear: (handle) => window.clearTimeout(handle), set: (fn, ms) => window.setTimeout(fn, ms) },
  view: {
    hide: () => {
      stopFollowing();
      labelElement().hidden = true;
    },
    show: (host, text) => {
      const el = labelElement();
      el.textContent = text;
      el.hidden = false;
      stopFollowing();
      if (host instanceof Element) stopFollowing = placeFloating(host, el, 'top');
    },
  },
});

const bindings = new WeakMap<HTMLElement, IHoverBinding<string>>();
let isListening = false;

/** `v-tooltip="'Back 10 seconds'"`: a one-line hover/focus label that is also the element's aria-label. */
const tooltipDirective: DirectiveOptions = {
  bind(el, { value }) {
    if (!isListening) {
      isListening = true;
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') layer.dismiss();
      });
      window.addEventListener('scroll', layer.dismiss, true);
    }
    bindings.set(el, bindTooltip(layer, el, String(value)));
  },
  unbind(el) {
    bindings.get(el)?.unbind();
    bindings.delete(el);
  },
  update(el, { oldValue, value }) {
    if (value !== oldValue) bindings.get(el)?.setContent(String(value));
  },
};

export default tooltipDirective;
